import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { TextInput, TextArea, MultiSelectChips } from '../FormElements';
import { StepIndicator } from './StepIndicator';
import { FileUpload } from './FileUpload';
import { RangeSlider } from './RangeSlider';
const WEB3FORMS_KEY = '9d55d263-83bb-4e6f-b05a-3b8d38836fdc';
interface OrganizerOnboardingProps {
  onComplete: (data: any) => void;
}
const STEPS = [{
  name: 'Your Story'
}, {
  name: 'Identity'
}, {
  name: 'Audience'
}, {
  name: 'Reach'
}, {
  name: 'Offering'
}, {
  name: 'Dream Partner'
}];
export function OrganizerOnboarding({
  onComplete
}: OrganizerOnboardingProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    concept: '',
    mission: '',
    website: '',
    instagram: '',
    audienceSize: 1000,
    ageMin: 18,
    ageMax: 65,
    interests: [] as string[],
    attendeeDesc: '',
    geoReach: '',
    media: [] as string[],
    highlight: '',
    opportunities: [] as string[],
    benefits: [] as string[],
    specialFactor: '',
    industries: [] as string[],
    values: [] as string[],
    idealSponsor: ''
  });
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const submitToEmail = async () => {
    setIsSubmitting(true);
    // Get the access code used to login
    const accessCode = localStorage.getItem('sponsrai_access') || 'Unknown';
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `🎪 Organizer Profile Complete: ${formData.name}`,
          from_name: 'SponsrAI Dashboard',
          to: 'info@sponsrai.se',
          'Profile Type': 'ORGANIZER PROFILE (Full)',
          'Access Code Used': accessCode,
          '--- STORY ---': '',
          'Event Name': formData.name,
          Tagline: formData.tagline,
          Concept: formData.concept,
          Mission: formData.mission,
          '--- IDENTITY ---': '',
          Website: formData.website || 'Not provided',
          Instagram: formData.instagram || 'Not provided',
          '--- AUDIENCE ---': '',
          'Expected Audience Size': formData.audienceSize.toLocaleString(),
          'Age Range': `${formData.ageMin} - ${formData.ageMax}`,
          'Audience Interests': formData.interests.join(', ') || 'Not specified',
          'Typical Attendee': formData.attendeeDesc || 'Not provided',
          '--- REACH ---': '',
          'Geographic Reach': formData.geoReach || 'Not specified',
          'Media Coverage': formData.media.join(', ') || 'Not specified',
          'Highlight/Achievement': formData.highlight || 'Not provided',
          '--- OFFERING ---': '',
          'Opportunities Available': formData.opportunities.join(', ') || 'Not specified',
          'Key Benefits': formData.benefits.join(', ') || 'Not specified',
          'What Makes Them Special': formData.specialFactor || 'Not provided',
          '--- DREAM PARTNER ---': '',
          'Target Industries': formData.industries.join(', ') || 'Not specified',
          'Values Alignment': formData.values.join(', ') || 'Not specified',
          'Ideal Sponsor Description': formData.idealSponsor || 'Not provided'
        })
      });
    } catch (err) {
      console.error('Failed to submit profile:', err);
    }
    setIsSubmitting(false);
    onComplete(formData);
  };
  const handleNext = () => {
    if (step < STEPS.length) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    } else {
      submitToEmail();
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };
  return <div className="max-w-3xl mx-auto" data-id="element-408">
      <StepIndicator steps={STEPS} currentStep={step} accentColor="purple" data-id="element-409" />

      <motion.div key={step} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 mb-8" data-id="element-410">
        {step === 1 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-411">
              Tell us about your event
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-412">
              What makes your concept unique?
            </p>

            <TextInput label="Event / Organization Name" placeholder="e.g. Future Tech Summit" value={formData.name} onChange={e => handleChange('name', e.target.value)} data-id="element-413" />
            <TextInput label="Tagline" placeholder="Your event in one sentence" value={formData.tagline} onChange={e => handleChange('tagline', e.target.value)} data-id="element-414" />
            <TextArea label="What is it?" placeholder="Describe the concept, atmosphere, and experience..." value={formData.concept} onChange={e => handleChange('concept', e.target.value)} data-id="element-415" />
            <TextArea label="Why does it exist?" placeholder="What's the mission? Why does it matter?" value={formData.mission} onChange={e => handleChange('mission', e.target.value)} data-id="element-416" />
          </>}

        {step === 2 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-417">
              Show us your event
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-418">
              Great visuals attract great sponsors.
            </p>

            <FileUpload label="Upload Logo" accentColor="purple" data-id="element-419" />

            <TextInput label="Website URL" placeholder="https://" value={formData.website} onChange={e => handleChange('website', e.target.value)} data-id="element-420" />
            <TextInput label="Instagram Handle" placeholder="@yourevent" value={formData.instagram} onChange={e => handleChange('instagram', e.target.value)} data-id="element-421" />
          </>}

        {step === 3 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-422">
              Who shows up?
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-423">
              Describe the people in the room.
            </p>

            <RangeSlider label="Expected Audience Size" min={100} max={10000} step={100} defaultValue={formData.audienceSize} formatValue={v => v.toLocaleString()} onChange={v => handleChange('audienceSize', v)} accentColor="purple" data-id="element-424" />

            <div className="mb-8" data-id="element-425">
              <RangeSlider label="Age Range (Min)" min={13} max={80} defaultValue={formData.ageMin} onChange={v => handleChange('ageMin', v)} accentColor="purple" data-id="element-426" />
              <RangeSlider label="Age Range (Max)" min={13} max={80} defaultValue={formData.ageMax} onChange={v => handleChange('ageMax', v)} accentColor="purple" data-id="element-427" />
            </div>

            <MultiSelectChips label="Audience Interests" options={['Tech', 'Business', 'Art', 'Music', 'Food', 'Fitness', 'Travel', 'Fashion', 'Sustainability', 'Gaming']} selectedValues={formData.interests} onChange={vals => handleChange('interests', vals)} accentColor="purple" data-id="element-428" />

            <TextInput label="Describe your typical attendee" placeholder="e.g. Young creatives looking for inspiration..." value={formData.attendeeDesc} onChange={e => handleChange('attendeeDesc', e.target.value)} data-id="element-429" />
          </>}

        {step === 4 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-430">
              What's your impact?
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-431">
              Show sponsors the scale of your reach.
            </p>

            <div className="mb-6" data-id="element-432">
              <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-433">
                Geographic Reach
              </label>
              <select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500" value={formData.geoReach} onChange={e => handleChange('geoReach', e.target.value)} data-id="element-434">
                <option value="" data-id="element-435">Select reach...</option>
                <option value="Local" data-id="element-436">Local</option>
                <option value="Regional" data-id="element-437">Regional</option>
                <option value="National" data-id="element-438">National</option>
                <option value="International" data-id="element-439">International</option>
              </select>
            </div>

            <MultiSelectChips label="Media Coverage" options={['Press', 'TV', 'Social Media', 'Podcasts', 'Influencers', 'Blogs']} selectedValues={formData.media} onChange={vals => handleChange('media', vals)} accentColor="purple" data-id="element-440" />

            <TextArea label="Share a highlight or achievement" placeholder="e.g. Last year we trended #1 on Twitter in London..." value={formData.highlight} onChange={e => handleChange('highlight', e.target.value)} data-id="element-441" />
          </>}

        {step === 5 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-442">
              What can sponsors get?
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-443">
              Define your inventory and value.
            </p>

            <MultiSelectChips label="Opportunities Available" options={['Logo Placement', 'Speaking Slot', 'Booth', 'Sampling', 'Digital', 'VIP Access', 'Naming Rights', 'Content']} selectedValues={formData.opportunities} onChange={vals => handleChange('opportunities', vals)} accentColor="purple" data-id="element-444" />

            <MultiSelectChips label="Key Benefits" options={['Visibility', 'Leads', 'Sales', 'Networking', 'Content', 'Community', 'Media', 'Data']} selectedValues={formData.benefits} onChange={vals => handleChange('benefits', vals)} accentColor="purple" data-id="element-445" />

            <TextArea label="What makes sponsoring you special?" placeholder="e.g. We offer direct access to C-level executives in a relaxed setting..." value={formData.specialFactor} onChange={e => handleChange('specialFactor', e.target.value)} data-id="element-446" />
          </>}

        {step === 6 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-447">
              Dream Partner
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-448">Who are you hoping to meet?</p>

            <MultiSelectChips label="Target Industries" options={['Tech', 'Fashion', 'Food', 'Finance', 'Health', 'Entertainment', 'Auto', 'Sports', 'Travel']} selectedValues={formData.industries} onChange={vals => handleChange('industries', vals)} accentColor="purple" data-id="element-449" />

            <MultiSelectChips label="Values Alignment" options={['Sustainability', 'Innovation', 'Diversity', 'Health', 'Education', 'Entertainment', 'Social Impact']} selectedValues={formData.values} onChange={vals => handleChange('values', vals)} accentColor="purple" data-id="element-450" />

            <TextArea label="What would make a brand perfect for your event?" placeholder="e.g. A brand that wants to co-create content rather than just put a logo on a wall..." value={formData.idealSponsor} onChange={e => handleChange('idealSponsor', e.target.value)} data-id="element-451" />
          </>}
      </motion.div>

      <div className="flex justify-between" data-id="element-452">
        <button onClick={handleBack} disabled={step === 1 || isSubmitting} className={`px-6 py-3 rounded-full font-medium transition-colors flex items-center gap-2 ${step === 1 || isSubmitting ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-453">
          <ArrowLeft size={18} data-id="element-454" /> Back
        </button>
        <button onClick={handleNext} disabled={isSubmitting} className="px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-full font-bold transition-all shadow-[0_0_20px_rgba(168,85,247,0.3)] flex items-center gap-2 disabled:opacity-50" data-id="element-455">
          {isSubmitting ? <>
              <Loader2 size={18} className="animate-spin" data-id="element-456" /> Saving...
            </> : <>
              {step === STEPS.length ? 'Finish Profile' : 'Next Step'}{' '}
              <ArrowRight size={18} data-id="element-457" />
            </>}
        </button>
      </div>
    </div>;
}